import { ChangeEvent, FC, useEffect, useState } from 'react';
import styles from './input-box.module.scss';
import useThread from '../../pages/Thread/hooks/useThread';
import SendIcon from '@/assets/icons/send.svg?react';
import ClockIcon from '@/assets/icons/clock.svg?react';

import InputFilters from '../InputFilters';
import { classnames, getTodayStr } from '@/utils/functions';
import DeepDiveToggle from '../DeepDiveToggle';
import DeepDiveHint from '../DeepDiveHint';
import Modal from '../Modal/modal';
import useSession from '@/hooks/useSession';
import Button from '../Button';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ApproachRouteState,
  DEEP_DIVE_INDEX,
  PROMPT_APPROACHES,
  QUICK_FLIGHT_INDEX,
} from '@/models/thread';

interface InputBoxProps {
  showFilters?: boolean;
}

const InputBox: FC<InputBoxProps> = ({ showFilters = false }) => {
  const { id: routeParamID } = useParams();
  const { selectedApproach, setSelectedApproach } = useThread();
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [earliestDate, setEarliestDate] = useState<string>('');
  const [latestDate, setLatestDate] = useState<string>(getTodayStr());
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const { pushUserEvent } = useSession();
  const { status, handlePromptSubmit, id, deepDiveAutoSelect, promptRef } =
    useThread();
  const location = useLocation();
  const navigate = useNavigate();
  // While Deep Dive is being turned on for the user, hold sending and
  // toggling so neither acts on the approach that is about to change
  const { isPending: isAutoSelecting } = deepDiveAutoSelect;
  const disableSubmit =
    prompt.trim() === '' || status !== 'READY' || isAutoSelecting;
  const isDeepDive = selectedApproach === DEEP_DIVE_INDEX;

  // The event prefix tells apart where the change came from: 'toggle' for
  // the Deep Dive switch, 'select' for a pick in the modal.
  const selectApproach = (index: number, source: 'toggle' | 'select') => {
    if (isAutoSelecting) return;
    deepDiveAutoSelect.hideHint();
    if (index === selectedApproach) return;
    setSelectedApproach(index);
    pushUserEvent(`${source}_approach_${PROMPT_APPROACHES[index].id}`);
  };

  useEffect(() => {
    if (promptRef.current) {
      promptRef.current.focus();
    }
  }, [id, promptRef]);

  // Another page can send the user here with an approach picked, such as the
  // banner's Try Deep Dive. It's applied once the box is on the page, and
  // then cleared so a reload or a return here doesn't apply it again.
  useEffect(() => {
    const approach = (location.state as ApproachRouteState | null)?.approach;
    if (!approach) return;
    const index = PROMPT_APPROACHES.findIndex(({ id }) => id === approach);
    if (index !== -1) setSelectedApproach(index);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate, setSelectedApproach]);

  useEffect(() => {
    return () => {
      if (routeParamID !== 'new' && typeof routeParamID !== 'undefined') {
        setPrompt('');
        setSelectedApproach(0);
      }
    };
  }, [routeParamID, setSelectedApproach]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (
      !(event.shiftKey || event.ctrlKey || event.metaKey) &&
      event.key === 'Enter' &&
      !disableSubmit
    ) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const resetInputHeight = () => {
    if (!promptRef.current) return;

    promptRef.current.style.height = '0px';
    const scrollHeight = promptRef.current.scrollHeight;
    promptRef.current.style.height = scrollHeight + 4 + 'px';
  };
  const onPromptChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(event.target.value);
    resetInputHeight();
  };

  const resetPrompt = () => {
    setPrompt('');
    setTimeout(() => {
      resetInputHeight();
    }, 20);
  };

  const handleSubmit = () => {
    if (disableSubmit || !promptRef.current) return;
    deepDiveAutoSelect.hideHint();
    promptRef.current.blur();
    resetPrompt();
    let filters = undefined;

    if (showFilters) {
      filters = {
        countries: selectedCountries,
        sources: selectedSources,
        earliestDate,
        latestDate,
      };
    }
    handlePromptSubmit(promptRef.current.value, filters);
  };

  return (
    // The box clips its content, so the hint sits on this wrapper instead
    <div className={styles['input-box-anchor']}>
      <DeepDiveHint
        isVisible={deepDiveAutoSelect.isHintVisible}
        onHide={deepDiveAutoSelect.hideHint}
        onDismiss={deepDiveAutoSelect.dismissHint}
      />
      <div className={classnames(styles['input-box'])}>
        <div className={styles['input-box-bar']}>
          <DeepDiveToggle
            className={styles['deep-dive-section']}
            isOn={isDeepDive}
            isLocked={isAutoSelecting}
            onToggle={(isOn) =>
              selectApproach(
                isOn ? DEEP_DIVE_INDEX : QUICK_FLIGHT_INDEX,
                'toggle'
              )
            }
            onInfoClick={() => {
              // A pick in the modal would be ignored during the hold
              if (isAutoSelecting) return;
              setIsOpen(true);
              pushUserEvent('open_deep_dive_info');
            }}
          />
          {createPortal(
            <Modal
              title="Select Approach"
              size="small"
              isOpen={isOpen}
              onClose={() => {
                setIsOpen(false);
              }}
            >
              <section className={styles['approach-modal-content']}>
                {PROMPT_APPROACHES.map((approach, index) => (
                  <button
                    key={index}
                    className={classnames(styles['approach-option'], {
                      [styles['selected']]: selectedApproach === index,
                    })}
                    onClick={() => {
                      selectApproach(index, 'select');
                      setIsOpen(false);
                    }}
                  >
                    {approach.new && (
                      <strong className={styles['badge']}>NEW</strong>
                    )}
                    <p>
                      <span className={styles['approach-label']}>
                        <i>{approach.emoji}</i>
                        {approach.label}
                      </span>
                      <span className={styles['approach-time-estimate']}>
                        <ClockIcon
                          className={styles['approach-time-estimate-icon']}
                        />
                        <strong>Wait time:</strong> ~{approach.timeEstimate}
                      </span>
                      {approach.description}
                    </p>
                  </button>
                ))}
              </section>
              <Button variation="secondary" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </Modal>,
            document.body
          )}
          {showFilters && (
            <>
              <InputFilters
                selectedCountries={selectedCountries}
                selectedSources={selectedSources}
                earliestDate={earliestDate}
                latestDate={latestDate}
                setSelectedCountries={setSelectedCountries}
                setSelectedSources={setSelectedSources}
                setEarliestDate={setEarliestDate}
                setLatestDate={setLatestDate}
              />
            </>
          )}
        </div>

        <textarea
          data-testid="input-box"
          name="prompt"
          ref={promptRef}
          value={prompt}
          className={styles['prompt']}
          onChange={onPromptChange}
          rows={1}
          tabIndex={0}
          autoFocus={true}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
        ></textarea>
        <button
          data-testid="submit-prompt"
          className={styles['submit-prompt']}
          onClick={handleSubmit}
          disabled={disableSubmit}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

export default InputBox;
